import { Injectable } from '@angular/core';

import { Observable, catchError, of, shareReplay, merge } from 'rxjs';
import { switchMap, tap, map, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { DistributionSummaryModel } from '../model/distribution-summary-model';
import { DistributionApi } from '../../../api/distributions/distribution.api';
import { DistributionModel } from '../../../Discover/Targeting/models/distribution.model';
import { DistributionType } from '../enum/distribution-type.enum';
import { ResourceAction } from '../../Basic/service/resource-abstract.service';
import { AllResourceAbstractService } from '../../Basic/service/all-resource-abstract.service';
import { DistributionApiModel } from '../../../api/distributions/model/distribution-api.model';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { ResourceActionTypes } from '../../Basic/enums/resource-actions.enum';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { sortByDate } from '../../Misc/helpers/sort/sort.helper';
import { DistributionSummaryApiModel } from '../../../api/distributions/model/distribution-summary-api.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { CommonApiErrorEnum } from '@api/genericApi/enums/common-api-error.enum';


@Injectable()
export class DistributionTemplateService
    extends AllResourceAbstractService<DistributionModel, DistributionApiModel, DistributionSummaryModel, DistributionSummaryApiModel> {


    all$: Observable<DistributionSummaryModel[]> = this.userStateService.hasAnyPermissions(
        [userPermissionsEnum.advancedTargeting]
    ).pipe(
        switchMap((hasPermission) =>
            hasPermission ? merge(
                this.fetchList$(),
                this.refreshAllManualSubject
            ) : of([])
        ),
        shareReplay(1)
    );

    readonly TEMPLATE_SLUG: string = '[TEMPLATE]';

    constructor(
        private distributionApiService: DistributionApi,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private userStateService: CurrentUserStateService
    ) {
        super();
    }

    protected fetchList$(): Observable<DistributionSummaryModel[]> {
        return this.distributionApiService.getTemplates().pipe(
            map(list => list
                .map(item => this.constructListModel(item))
                .sort(sortByDate)
            ),
            catchError((error) => {
                if (error.status === 404) {
                    return of([]);
                }
                throw error;
            })
        );
    }

    getApiService(): MultiBaseAbstractApi<DistributionApiModel, DistributionSummaryApiModel> {
        return this.distributionApiService;
    }

    constructResourceModel(apiModel: DistributionApiModel): DistributionModel {
        const res = new DistributionModel(apiModel);
        res.name = this.stripTemplateSlug(res.name);

        return res;
    }


    constructListModel(apiModel: DistributionSummaryApiModel): DistributionSummaryModel {
        const res = new DistributionSummaryModel(apiModel);
        res.name = this.stripTemplateSlug(res.name);

        return res;
    }

    refreshAllManual(): Observable<DistributionSummaryModel[]> {
        return this.fetchList$().pipe(
            tap((list) => this.refreshAllManualSubject.next(list)),
        );
    }

    create(distribution: DistributionModel): Observable<number> {
        const template = this.convertToTemplate(distribution);

        return this.isNameDuplicated$(template.name).pipe(
            switchMap(isDuplicate => {
                if (isDuplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.distribution.saveAsTemplate.error.duplicateName.heading',
                        detail: this.localizationService.transform('Discover.distribution.saveAsTemplate.error.duplicateName.message', {
                            name: template.name
                        })
                    });

                    throw new Error(CommonApiErrorEnum.duplicateName);
                }

                template.name = this.appendPrefix(template.name);

                return super.createResource(template).pipe(
                    switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
                );
            })
        );
    }

    update(distribution: DistributionModel): Observable<number> {
        const template = this.convertToTemplate(distribution);
        const action: ResourceAction = { actionType: ResourceActionTypes.UPDATE, resource: template };

        return this.all$.pipe(
            take(1),
            switchMap(all => {
                const existing = all.find(x => x.id === template.id);
                const nameChanged = existing?.name !== template.name;

                if (!nameChanged) {
                    template.name = this.appendPrefix(template.name);
                    return super.updateResource(template, action);
                }

                return this.isNameDuplicated$(template.name, template.id).pipe(
                    switchMap(isDuplicate => {
                        if (isDuplicate) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Discover.distribution.saveAsTemplate.error.duplicateName.heading',
                                detail: this.localizationService.transform('Discover.distribution.saveAsTemplate.error.duplicateName.message', {
                                    name: template.name
                                })
                            });

                            throw new Error(CommonApiErrorEnum.duplicateName);
                        }

                        template.name = this.appendPrefix(template.name);

                        return super.updateResource(template, action);
                    })
                );
            }),
            switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
        );
    }


    delete(template: DistributionSummaryModel, broadcast: boolean): Observable<number> {
        const action: ResourceAction = { actionType: ResourceActionTypes.DELETE, resource: template };

        return super.deleteResource(template.id, action, broadcast).pipe(
            switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
        );
    }



    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource;
        const name = this.stripTemplateSlug(resource?.name || '');

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.distribution.template.create.error.heading',
                        detail: this.localizationService.transform('Discover.distribution.template.create.error.message', { name })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.distribution.template.create.heading',
                        detail: this.localizationService.transform('Discover.distribution.template.create.message', { name })
                    });
                }

                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.distribution.template.update.error.heading',
                        detail: this.localizationService.transform('Discover.distribution.template.update.error.message', { name })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.distribution.template.update.heading',
                        detail: this.localizationService.transform('Discover.distribution.template.update.message', { name })
                    });
                }

                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.distribution.template.delete.error.heading',
                        detail: this.localizationService.transform('Discover.distribution.template.delete.error.message', { name })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.distribution.template.delete.heading',
                        detail: this.localizationService.transform('Discover.distribution.template.delete.message', { name })
                    });
                }

                break;
            }
        }
    }

    private convertToTemplate(distribution: DistributionModel): DistributionModel {
        distribution.guid = null;
        distribution.type = DistributionType.TEMPLATE;

        return distribution;
    }

    /**
     * remove the template slug to show the actual name
     */
    private stripTemplateSlug(name: string): string {
        return name.replace(this.TEMPLATE_SLUG, '');
    }

    /**
     * Since distribution templates share the same create & update endpoints,
     * a template prefix is required to differentiate templates from normal distributions.
     * Otherwise, if template name and distribution name are mixed and cause name duplicate error.
     */
    private appendPrefix(name: string): string {
        return this.TEMPLATE_SLUG + name;
    }
}
